/**
 * useFluxNodes Hook
 *
 * Custom React hook for managing FluxNode data and state
 * Includes real APY calculations, parallel assets, and reward history
 */

import { useQuery } from '@tanstack/react-query';
import { fluxNodeAPI } from '../services/fluxNodeApi';
import type { FluxNode } from '../types/fluxNode.types';
import type {
  ExtendedNetworkStats,
  ParallelAssetsSummary,
  RewardPayout
} from '../services/fluxNodeApi';

/**
 * Fetch FluxNodes for given wallet addresses
 */
export const useFluxNodes = (walletAddresses: string[]) => {
  const {
    data: nodes,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['fluxnodes', walletAddresses],
    queryFn: () => fluxNodeAPI.getNodesByWallets(walletAddresses),
    enabled: walletAddresses.length > 0,
    staleTime: 30_000, // 30 seconds
    refetchInterval: 60_000, // Refetch every 60 seconds
  });

  // Calculate aggregated metrics
  const activeNodes = nodes?.filter(
    node => fluxNodeAPI.getNodeStatus(node) === 'CONFIRMED'
  ).length || 0;

  const offlineNodes = nodes?.filter(
    node => fluxNodeAPI.getNodeStatus(node) === 'OFFLINE'
  ).length || 0;

  // Group by tier
  const nodesByTier = {
    CUMULUS: nodes?.filter(n => n.tier === 'CUMULUS').length || 0,
    NIMBUS: nodes?.filter(n => n.tier === 'NIMBUS').length || 0,
    STRATUS: nodes?.filter(n => n.tier === 'STRATUS').length || 0
  };

  return {
    nodes: nodes || [],
    isLoading,
    error,
    refetch,
    totalNodes: nodes?.length || 0,
    activeNodes,
    offlineNodes,
    nodesByTier
  };
};

/**
 * Fetch network statistics
 */
export const useNetworkStats = () => {
  return useQuery({
    queryKey: ['fluxnetwork', 'stats'],
    queryFn: () => fluxNodeAPI.getNetworkStats(),
    staleTime: 5 * 60_000, // 5 minutes
    refetchInterval: 10 * 60_000, // Refetch every 10 minutes
  });
};

/**
 * Fetch FLUX price
 */
export const useFluxPrice = () => {
  return useQuery({
    queryKey: ['flux', 'price'],
    queryFn: () => fluxNodeAPI.getFluxPrice(),
    staleTime: 60_000, // 1 minute
    refetchInterval: 2 * 60_000, // Refetch every 2 minutes
  });
};

/**
 * Calculate total rewards across all nodes (legacy - uses static calculation)
 */
export const useNodeRewards = (nodes: FluxNode[], fluxPrice: number) => {
  if (!nodes || nodes.length === 0) {
    return {
      daily: 0,
      weekly: 0,
      monthly: 0,
      yearly: 0,
      avgROI: 0,
      totalValue: 0
    };
  }

  const rewards = nodes.map(node => fluxNodeAPI.calculateRewards(node, fluxPrice));

  const daily = rewards.reduce((sum, r) => sum + r.daily, 0);
  const weekly = rewards.reduce((sum, r) => sum + r.weekly, 0);
  const monthly = rewards.reduce((sum, r) => sum + r.monthly, 0);
  const yearly = rewards.reduce((sum, r) => sum + r.yearly, 0);
  const avgROI = rewards.reduce((sum, r) => sum + r.roi, 0) / rewards.length;

  // Calculate total collateral value (collateral is determined by tier)
  const tierCollaterals: Record<string, number> = {
    CUMULUS: 1000,
    NIMBUS: 12500,
    STRATUS: 40000
  };
  const totalCollateral = nodes.reduce((sum, node) => {
    return sum + (tierCollaterals[node.tier] || 0);
  }, 0);

  const totalValue = totalCollateral * fluxPrice;

  return {
    daily,
    weekly,
    monthly,
    yearly,
    avgROI,
    totalValue,
    monthlyUSD: monthly * fluxPrice,
    yearlyUSD: yearly * fluxPrice
  };
};

// Network stats history storage key
const NETWORK_STATS_HISTORY_KEY = 'flux_network_stats_history';

interface NetworkStatsHistoryEntry {
  date: string; // YYYY-MM-DD
  totalNodes: number;
  cumulusCount: number;
  nimbusCount: number;
  stratusCount: number;
}

// Save network stats to history (once per day)
const saveNetworkStatsToHistory = (stats: ExtendedNetworkStats) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const historyRaw = localStorage.getItem(NETWORK_STATS_HISTORY_KEY);
    const history: NetworkStatsHistoryEntry[] = historyRaw ? JSON.parse(historyRaw) : [];

    // Check if we already have data for today
    const existingIndex = history.findIndex(h => h.date === today);
    const entry: NetworkStatsHistoryEntry = {
      date: today,
      totalNodes: stats.totalNodes,
      cumulusCount: stats.cumulusCount || 0,
      nimbusCount: stats.nimbusCount || 0,
      stratusCount: stats.stratusCount || 0,
    };

    if (existingIndex >= 0) {
      history[existingIndex] = entry;
    } else {
      history.push(entry);
    }

    // Keep only last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const filtered = history.filter(h => new Date(h.date) >= thirtyDaysAgo);

    localStorage.setItem(NETWORK_STATS_HISTORY_KEY, JSON.stringify(filtered));
  } catch (e) {
    console.error('Failed to save network stats history:', e);
  }
};

// Get 7-day variations
export const getNetworkStats7DayVariation = (): {
  total: number | null;
  cumulus: number | null;
  nimbus: number | null;
  stratus: number | null;
} | null => {
  try {
    const historyRaw = localStorage.getItem(NETWORK_STATS_HISTORY_KEY);
    if (!historyRaw) return null;

    const history: NetworkStatsHistoryEntry[] = JSON.parse(historyRaw);
    if (history.length < 2) return null;

    // Sort by date descending
    history.sort((a, b) => b.date.localeCompare(a.date));

    const today = history[0];

    // Find entry from ~7 days ago (within 5-9 day range)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const targetDate = sevenDaysAgo.toISOString().split('T')[0];

    // Find closest entry to 7 days ago
    let oldEntry: NetworkStatsHistoryEntry | null = null;
    for (const entry of history) {
      if (entry.date <= targetDate) {
        oldEntry = entry;
        break;
      }
    }

    if (!oldEntry) {
      // Use oldest available if we don't have 7-day data
      oldEntry = history[history.length - 1];
      if (oldEntry.date === today.date) return null;
    }

    return {
      total: today.totalNodes - oldEntry.totalNodes,
      cumulus: today.cumulusCount - oldEntry.cumulusCount,
      nimbus: today.nimbusCount - oldEntry.nimbusCount,
      stratus: today.stratusCount - oldEntry.stratusCount,
    };
  } catch (e) {
    console.error('Failed to get network stats variation:', e);
    return null;
  }
};

/**
 * Fetch extended network statistics including real APY
 */
export const useExtendedNetworkStats = () => {
  return useQuery({
    queryKey: ['fluxnetwork', 'extended-stats'],
    queryFn: async () => {
      const stats = await fluxNodeAPI.getExtendedNetworkStats();
      // Save to history for 7-day comparison
      saveNetworkStatsToHistory(stats);
      return stats;
    },
    staleTime: 2 * 60_000, // 2 minutes
    refetchInterval: 5 * 60_000, // Refetch every 5 minutes
  });
};

/**
 * Fetch parallel assets for wallet addresses
 */
export const useParallelAssets = (walletAddresses: string[]) => {
  return useQuery({
    queryKey: ['flux', 'parallel-assets', walletAddresses],
    queryFn: async () => {
      const results: ParallelAssetsSummary[] = [];
      for (const address of walletAddresses) {
        const assets = await fluxNodeAPI.getParallelAssets(address);
        if (assets) {
          results.push(assets);
        }
      }
      return results;
    },
    enabled: walletAddresses.length > 0,
    staleTime: 5 * 60_000, // 5 minutes
    refetchInterval: 10 * 60_000, // Refetch every 10 minutes
  });
};

/**
 * Fetch reward payouts history for wallet addresses
 */
export const useRewardPayouts = (walletAddresses: string[]) => {
  return useQuery({
    queryKey: ['flux', 'reward-payouts', walletAddresses],
    queryFn: async () => {
      const allPayouts: RewardPayout[] = [];
      for (const address of walletAddresses) {
        const payouts = await fluxNodeAPI.getRewardPayouts(address);
        allPayouts.push(...payouts);
      }
      // Sort by timestamp descending
      return allPayouts.sort((a, b) => b.timestamp - a.timestamp);
    },
    enabled: walletAddresses.length > 0,
    staleTime: 2 * 60_000, // 2 minutes
    refetchInterval: 5 * 60_000, // Refetch every 5 minutes
  });
};

/**
 * Wallet balance data per address
 */
export interface WalletBalanceData {
  address: string;
  balance: number; // Current balance in FLUX
}

/**
 * Fetch wallet balances for all addresses
 * Returns balance per wallet and aggregated totals
 */
export const useWalletBalances = (walletAddresses: string[]) => {
  return useQuery({
    queryKey: ['flux', 'wallet-balances', walletAddresses],
    queryFn: async () => {
      const balances: WalletBalanceData[] = [];
      for (const address of walletAddresses) {
        const balance = await fluxNodeAPI.getWalletBalance(address);
        balances.push({ address, balance });
      }
      return balances;
    },
    enabled: walletAddresses.length > 0,
    staleTime: 60_000, // 1 minute
    refetchInterval: 2 * 60_000, // Refetch every 2 minutes
  });
};

/**
 * Calculate real rewards based on network stats (uses actual APY)
 */
export const useRealNodeRewards = (
  nodes: FluxNode[],
  extendedStats: ExtendedNetworkStats | undefined
) => {
  if (!nodes || nodes.length === 0 || !extendedStats) {
    return {
      daily: 0,
      weekly: 0,
      monthly: 0,
      yearly: 0,
      dailyUSD: 0,
      monthlyUSD: 0,
      yearlyUSD: 0,
      avgROI: 0,
      totalValue: 0,
      byTier: {
        CUMULUS: { apy: 0, daily: 0, monthly: 0 },
        NIMBUS: { apy: 0, daily: 0, monthly: 0 },
        STRATUS: { apy: 0, daily: 0, monthly: 0 }
      }
    };
  }

  const tierCollaterals: Record<string, number> = {
    CUMULUS: 1000,
    NIMBUS: 12500,
    STRATUS: 40000
  };

  // Get node counts by tier
  const nodesByTier = {
    CUMULUS: nodes.filter(n => n.tier === 'CUMULUS').length,
    NIMBUS: nodes.filter(n => n.tier === 'NIMBUS').length,
    STRATUS: nodes.filter(n => n.tier === 'STRATUS').length
  };

  // Calculate real rewards per tier
  const cumulusRewards = fluxNodeAPI.calculateRealRewards('CUMULUS', extendedStats.cumulusCount, extendedStats.fluxPrice);
  const nimbusRewards = fluxNodeAPI.calculateRealRewards('NIMBUS', extendedStats.nimbusCount, extendedStats.fluxPrice);
  const stratusRewards = fluxNodeAPI.calculateRealRewards('STRATUS', extendedStats.stratusCount, extendedStats.fluxPrice);

  // Aggregate across user's nodes
  const daily =
    cumulusRewards.daily * nodesByTier.CUMULUS +
    nimbusRewards.daily * nodesByTier.NIMBUS +
    stratusRewards.daily * nodesByTier.STRATUS;

  const monthly = daily * 30;
  const yearly = daily * 365;

  // Calculate total collateral
  const totalCollateral =
    nodesByTier.CUMULUS * tierCollaterals.CUMULUS +
    nodesByTier.NIMBUS * tierCollaterals.NIMBUS +
    nodesByTier.STRATUS * tierCollaterals.STRATUS;

  const totalValue = totalCollateral * extendedStats.fluxPrice;
  const avgROI = totalCollateral > 0 ? (yearly / totalCollateral) * 100 : 0;

  return {
    daily,
    weekly: daily * 7,
    monthly,
    yearly,
    dailyUSD: daily * extendedStats.fluxPrice,
    monthlyUSD: monthly * extendedStats.fluxPrice,
    yearlyUSD: yearly * extendedStats.fluxPrice,
    avgROI,
    totalValue,
    byTier: {
      CUMULUS: {
        apy: extendedStats.cumulusAPY,
        daily: cumulusRewards.daily,
        monthly: cumulusRewards.monthly
      },
      NIMBUS: {
        apy: extendedStats.nimbusAPY,
        daily: nimbusRewards.daily,
        monthly: nimbusRewards.monthly
      },
      STRATUS: {
        apy: extendedStats.stratusAPY,
        daily: stratusRewards.daily,
        monthly: stratusRewards.monthly
      }
    }
  };
};

/**
 * Aggregate parallel assets data
 */
export const useAggregatedParallelAssets = (parallelAssets: ParallelAssetsSummary[] | undefined) => {
  if (!parallelAssets || parallelAssets.length === 0) {
    return {
      totalClaimable: 0,
      totalClaimed: 0,
      totalReceived: 0,
      assetsByChain: {} as Record<string, { claimable: number; claimed: number; received: number }>
    };
  }

  let totalClaimable = 0;
  let totalClaimed = 0;
  let totalReceived = 0;
  const assetsByChain: Record<string, { claimable: number; claimed: number; received: number }> = {};

  for (const summary of parallelAssets) {
    totalClaimable += summary.totalClaimable;
    totalClaimed += summary.totalClaimed;
    totalReceived += summary.totalReceived;

    for (const asset of summary.assets) {
      if (!assetsByChain[asset.chain]) {
        assetsByChain[asset.chain] = { claimable: 0, claimed: 0, received: 0 };
      }
      assetsByChain[asset.chain].claimable += asset.claimable;
      assetsByChain[asset.chain].claimed += asset.claimed;
      assetsByChain[asset.chain].received += asset.received;
    }
  }

  return {
    totalClaimable,
    totalClaimed,
    totalReceived,
    assetsByChain
  };
};

/**
 * Fetch Datavex hosting prices
 */
export const useDatavexHostingPrices = () => {
  return useQuery({
    queryKey: ['datavex', 'hosting-prices'],
    queryFn: async () => {
      const { fetchDatavexPlatformStats } = await import('../services/datavexApi');
      return fetchDatavexPlatformStats();
    },
    staleTime: 5 * 60_000, // 5 minutes - prices don't change often
    refetchInterval: 30 * 60_000, // Refetch every 30 minutes
  });
};

/**
 * Fetch list of IPs hosted by Datavex
 * Returns a Set<string> for quick IP lookup
 */
export const useDatavexHostedIps = () => {
  return useQuery({
    queryKey: ['datavex', 'hosted-ips'],
    queryFn: async () => {
      const { fetchDatavexHostedIps } = await import('../services/datavexApi');
      return fetchDatavexHostedIps();
    },
    staleTime: 10 * 60_000, // 10 minutes - IPs don't change often
    refetchInterval: 60 * 60_000, // Refetch every hour
  });
};

/**
 * Fetch Datavex hosted nodes with expiration info
 * Returns a Map<string, DatavexExpirationInfo> for quick IP lookup
 */
export const useDatavexExpirations = () => {
  return useQuery({
    queryKey: ['datavex', 'expirations'],
    queryFn: async () => {
      const { fetchDatavexHostedNodesWithExpiration } = await import('../services/datavexApi');
      return fetchDatavexHostedNodesWithExpiration();
    },
    staleTime: 10 * 60_000, // 10 minutes
    refetchInterval: 30 * 60_000, // Refetch every 30 minutes
  });
};
